/**
 * The rows whose shown values a value edit can change — the precise form of
 * the `cell-content-column` scope, which re-reads the edited column in every
 * row. A `ColumnDataChanged` names the row its cell was written in; the
 * closure from that row is:
 *
 * - **same column** (the edited period only): the row, every row that shows
 *   the same data (rows with the same data owner: aliases and projections of
 *   a source, the source of an alias), and their descendants (renderer and
 *   virtual children paint their parent's data);
 * - **whole row** (every column): every ancestor of those that does not show
 *   the edited data itself (an aggregate or computed parent may fold any
 *   period into any other), every formula row
 *   that reads any row of the closure, transitively (a formula may read
 *   other periods: shifts, running totals), with their descendants and
 *   ancestors; and the edited rows themselves when they hold events (an
 *   event keyed on one day can span several, so its overlay crosses columns).
 *
 * It answers `null` — keep the column-wide re-read — when any source is not a
 * row-naming `ColumnDataChanged` or names a row the sheet does not hold. It
 * never answers fewer rows than can change: when in doubt a row is in the
 * closure, and in doubt about the period, the whole row.
 *
 * @module
 */
import { NaiveDate } from "@tento-chrono";
import type { Option } from "../../core/option.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";
import { type Invalidation, ColumnDataChanged } from "../../tx/invalidation.ts";
import type { SheetModelReads } from "../../sheet/rows-sheet-view.ts";
import { liveRowOf } from "../../rows/model-row.ts";
import { SourceRow } from "../../rows/variants/source-row.ts";
import { EVENTS_FACADE } from "../../facades/events-facade.ts";
import { collectRowContentRanges, type RowContentRanges } from "./row-content-scope.ts";

/** The rows a value edit can change (see the module doc). */
export namespace ValueEditScope {
  /**
   * The sheet facts the closure reads. `ValueEditScope.sheet` builds it from
   * a live sheet; tests may hand a plain one.
   */
  export interface Sheet {
    /** Every row id (roots and descendants). */
    rowIds(): Iterable<RowLocalId>;
    /** Whether the sheet holds `id`. */
    has(id: RowLocalId): boolean;
    parentOf(id: RowLocalId): Option<RowLocalId>;
    childrenOf(id: RowLocalId): readonly RowLocalId[];
    /** The rows whose formulas read `id` directly. */
    dependentsOf(id: RowLocalId): Iterable<RowLocalId>;
    /** The row whose cells `id` shows (itself for a source; `null` when it shows none). */
    dataOwnerOf(id: RowLocalId): Option<RowLocalId>;
    /** Whether `id` holds events (cells whose overlay can span columns). */
    holdsEvents(id: RowLocalId): boolean;
    /**
     * The rows other than `owner` whose data owner is `owner`, from an index
     * the sheet keeps across edits (`SheetModelReads.peekDataMirrorsOf`). Absent,
     * `of` derives it from `rowIds` and `dataOwnerOf`, a pass over every row.
     */
    mirrorsOf?(owner: RowLocalId): readonly RowLocalId[];
  }

  /** The re-read rows for a drained `cell-content-column` batch, or `null` to keep the column-wide re-read. */
  export function of(sheet: Sheet, sources: readonly Invalidation[]): Option<ReadonlyMap<RowLocalId, RowContentRanges>> {
    const mirrors = mirrorsOf(sheet);
    const edits: { rowId: RowLocalId; range: NaiveDate.Range }[] = [];
    for (const s of sources) {
      // A write may name the canonical owner rather than a row of this sheet: its mirrors are here.
      if (!(s instanceof ColumnDataChanged) || s.rowId == null || !(sheet.has(s.rowId) || mirrors(s.rowId).length > 0)) {
        return null;
      }
      edits.push({ rowId: s.rowId, range: new NaiveDate.Range(s.column.start, s.column.end) });
    }
    if (edits.length === 0) return null;
    const changes: { rowId: RowLocalId; range: Option<NaiveDate.Range> }[] = [];
    const whole = new Set<RowLocalId>();
    const reached = new Set<RowLocalId>();
    // Rows that show the edited data itself: a renderer child's parent is the row it paints, not an aggregate.
    const shownData = new Set<RowLocalId>();
    for (const { rowId, range } of edits) {
      const same = new Set<RowLocalId>();
      const owner = sheet.has(rowId) ? sheet.dataOwnerOf(rowId) : rowId;
      const shown = [rowId, ...(owner == null ? [] : [owner, ...mirrors(owner)])];
      for (const id of shown) if (sheet.has(id)) addTree(sheet, id, same);
      const events = [...same].some((id) => sheet.holdsEvents(id));
      for (const id of same) {
        shownData.add(id);
        changes.push({ rowId: id, range: events ? null : range });
        reached.add(id);
        if (events) whole.add(id);
      }
    }
    // Whole rows: ancestors of anything reached, and formula readers of anything reached, to a fixpoint.
    const queue = [...reached];
    for (let id = queue.pop(); id != null; id = queue.pop()) {
      const add = (next: RowLocalId) => {
        if (whole.has(next)) return;
        whole.add(next);
        reached.add(next);
        queue.push(next);
      };
      for (let p = sheet.parentOf(id); p != null; p = sheet.parentOf(p)) {
        if (shownData.has(p)) continue;
        add(p);
        // The rows that show the ancestor's data (an alias or projection of an
        // aggregate) re-read whole with it (N-G).
        const owner = sheet.dataOwnerOf(p);
        if (owner == null) continue;
        const family = new Set<RowLocalId>();
        for (const m of [owner, ...mirrors(owner)]) if (m !== p && sheet.has(m)) addTree(sheet, m, family);
        for (const f of family) add(f);
      }
      for (const reader of sheet.dependentsOf(id)) {
        const family = new Set<RowLocalId>();
        addTree(sheet, reader, family);
        const owner = sheet.dataOwnerOf(reader);
        if (owner != null) for (const m of mirrors(owner)) addTree(sheet, m, family);
        for (const f of family) add(f);
      }
    }
    for (const id of whole) changes.push({ rowId: id, range: null });
    return collectRowContentRanges(changes);
  }
}

/** The closure's facts read from a sheet's reads (`platformOf(handle).sheet`). */
export namespace ValueEditScope {
  export function sheet(reads: SheetModelReads): Sheet {
    const source = (id: RowLocalId): Option<SourceRow> => {
      const row = reads.peekRow(id);
      if (row == null) return null;
      const live = liveRowOf(row);
      return live instanceof SourceRow ? live : null;
    };
    return {
      rowIds: () => reads.allRows().map((row) => row.id),
      has: (id) => reads.peekRow(id) != null,
      parentOf: (id) => reads.parentOf(id),
      childrenOf: (id) => reads.childrenOf(id),
      dependentsOf: (id) => reads.graph.getDirectDependents(id),
      dataOwnerOf: (id) => source(id)?.dataOwningRowId() ?? null,
      holdsEvents: (id) => source(id)?.as(EVENTS_FACADE) != null,
      mirrorsOf: (owner) => reads.peekDataMirrorsOf(owner),
    };
  }
}

/** Data owner -> the other rows showing its data: the sheet's own index, or one pass over its rows. */
function mirrorsOf(sheet: ValueEditScope.Sheet): (owner: RowLocalId) => readonly RowLocalId[] {
  if (sheet.mirrorsOf != null) return sheet.mirrorsOf.bind(sheet);
  const index = mirrorIndex(sheet);
  return (owner) => index.get(owner) ?? [];
}

/** Data owner -> the other rows showing its data, from one pass over every row. */
function mirrorIndex(sheet: ValueEditScope.Sheet): Map<RowLocalId, RowLocalId[]> {
  const out = new Map<RowLocalId, RowLocalId[]>();
  for (const id of sheet.rowIds()) {
    const owner = sheet.dataOwnerOf(id);
    if (owner == null || owner === id) continue;
    let list = out.get(owner);
    if (list == null) out.set(owner, list = []);
    list.push(id);
  }
  return out;
}

/** `id` and its descendants into `out`. */
function addTree(sheet: ValueEditScope.Sheet, id: RowLocalId, out: Set<RowLocalId>): void {
  if (out.has(id)) return;
  out.add(id);
  for (const child of sheet.childrenOf(id)) addTree(sheet, child, out);
}
